/**
 * Course Profile System
 *
 * Courses are characterised by which skills matter most, allowing
 * golfer-course fit calculations. Combined with the handicap system's
 * slope and course rating, this creates realistic difficulty modelling.
 */

/**
 * Course profile schema
 *
 * @typedef {Object} CourseProfile
 * @property {string} id - Unique identifier
 * @property {string} name - Course name
 * @property {Object} location - Geographic information
 * @property {number} par - Course par (typically 70-72)
 * @property {number} courseRating - Expected score for scratch golfer
 * @property {number} slopeRating - Relative difficulty (113 = average)
 * @property {number} yardage - Total length from championship tees
 * @property {Object} skillWeights - How much each skill matters (0-1)
 * @property {number} weatherExposure - Susceptibility to conditions (0-1)
 * @property {string} courseType - Classification for styling
 * @property {Array} holes - Individual hole configurations
 */

/**
 * Create a new course profile with defaults
 */
export const createCourseProfile = (overrides = {}) => ({
    id: overrides.id || `course-${Date.now()}`,
    name: overrides.name || "New Course",

    location: {
        country: "GBR",
        region: "",
        climate: "temperate",
        ...overrides.location
    },

    par: overrides.par ?? 72,
    courseRating: overrides.courseRating ?? 72.0,
    slopeRating: overrides.slopeRating ?? 125,
    yardage: overrides.yardage ?? 7000,

    // Skill importance weights (0-1, higher = more important)
    skillWeights: {
        distance: 0.5,
        accuracy: 0.5,
        approach: 0.5,
        aroundGreen: 0.5,
        putting: 0.5,
        ...overrides.skillWeights
    },

    weatherExposure: overrides.weatherExposure ?? 0.5,
    courseType: overrides.courseType || "parkland",

    holes: overrides.holes || generateDefaultHoles(overrides.par ?? 72)
});

/**
 * Generate default 18-hole layout
 * Standard par 72: 4×par3, 10×par4, 4×par5
 */
const generateDefaultHoles = (par) => {
    const parDistribution = par === 72
        ? [4, 3, 5, 4, 4, 4, 3, 4, 5, 4, 4, 3, 4, 5, 4, 4, 3, 5]  // Standard par 72
        : par === 71
            ? [4, 3, 5, 4, 4, 4, 3, 4, 4, 4, 4, 3, 4, 5, 4, 4, 3, 5]  // Par 71
            : par === 70
                ? [4, 3, 4, 4, 4, 4, 3, 4, 5, 4, 4, 3, 4, 5, 4, 4, 3, 4]  // Par 70
                : Array(18).fill(4);  // Fallback

    // Stroke indices (difficulty ranking, 1 = hardest)
    const strokeIndices = [7, 15, 1, 11, 5, 9, 17, 3, 13, 8, 16, 2, 12, 6, 10, 18, 4, 14];

    return parDistribution.map((holePar, index) => ({
        number: index + 1,
        par: holePar,
        strokeIndex: strokeIndices[index],
        yardage: getTypicalYardage(holePar),
        difficulty: (19 - strokeIndices[index]) / 18  // Normalised difficulty
    }));
};

/**
 * Typical yardages by par
 */
const getTypicalYardage = (par) => {
    switch (par) {
        case 3: return 160 + Math.floor(Math.random() * 60);  // 160-220
        case 4: return 380 + Math.floor(Math.random() * 80);  // 380-460
        case 5: return 520 + Math.floor(Math.random() * 80);  // 520-600
        default: return 400;
    }
};

/**
 * Famous course presets with realistic characteristics
 */
export const COURSE_PRESETS = {
    // Augusta National - Long, rewards distance and approach
    augustaNational: createCourseProfile({
        id: "augusta-national",
        name: "Augusta National",
        location: { country: "USA", region: "Georgia", climate: "subtropical" },
        par: 72,
        courseRating: 76.2,
        slopeRating: 137,
        yardage: 7510,
        skillWeights: {
            distance: 0.8,
            accuracy: 0.5,
            approach: 0.85,
            aroundGreen: 0.75,
            putting: 0.7
        },
        weatherExposure: 0.3,
        courseType: "parkland"
    }),

    // St Andrews Old Course - Links, wind management essential
    stAndrews: createCourseProfile({
        id: "st-andrews",
        name: "St Andrews Old Course",
        location: { country: "SCO", region: "Fife", climate: "maritime" },
        par: 72,
        courseRating: 73.1,
        slopeRating: 132,
        yardage: 7305,
        skillWeights: {
            distance: 0.5,
            accuracy: 0.7,
            approach: 0.7,
            aroundGreen: 0.85,
            putting: 0.6
        },
        weatherExposure: 0.9,
        courseType: "links"
    }),

    // Pebble Beach - Coastal, demands precision
    pebbleBeach: createCourseProfile({
        id: "pebble-beach",
        name: "Pebble Beach",
        location: { country: "USA", region: "California", climate: "mediterranean" },
        par: 72,
        courseRating: 75.5,
        slopeRating: 145,
        yardage: 7075,
        skillWeights: {
            distance: 0.55,
            accuracy: 0.8,
            approach: 0.8,
            aroundGreen: 0.7,
            putting: 0.65
        },
        weatherExposure: 0.7,
        courseType: "coastal"
    }),

    // Royal Birkdale - Traditional links
    royalBirkdale: createCourseProfile({
        id: "royal-birkdale",
        name: "Royal Birkdale",
        location: { country: "GBR", region: "Merseyside", climate: "maritime" },
        par: 72,
        courseRating: 75.0,
        slopeRating: 138,
        yardage: 7156,
        skillWeights: {
            distance: 0.6,
            accuracy: 0.75,
            approach: 0.7,
            aroundGreen: 0.8,
            putting: 0.6
        },
        weatherExposure: 0.85,
        courseType: "links"
    }),

    // TPC Sawgrass - Target golf, accuracy premium
    tpcSawgrass: createCourseProfile({
        id: "tpc-sawgrass",
        name: "TPC Sawgrass",
        location: { country: "USA", region: "Florida", climate: "subtropical" },
        par: 72,
        courseRating: 76.8,
        slopeRating: 155,
        yardage: 7245,
        skillWeights: {
            distance: 0.5,
            accuracy: 0.9,
            approach: 0.85,
            aroundGreen: 0.65,
            putting: 0.75
        },
        weatherExposure: 0.4,
        courseType: "parkland"
    }),

    // Royal Portrush - Exposed links
    royalPortrush: createCourseProfile({
        id: "royal-portrush",
        name: "Royal Portrush",
        location: { country: "NIR", region: "Antrim", climate: "maritime" },
        par: 72,
        courseRating: 75.2,
        slopeRating: 142,
        yardage: 7337,
        skillWeights: {
            distance: 0.65,
            accuracy: 0.7,
            approach: 0.7,
            aroundGreen: 0.85,
            putting: 0.55
        },
        weatherExposure: 0.95,
        courseType: "links"
    }),

    // Wentworth West - European Tour classic
    wentworthWest: createCourseProfile({
        id: "wentworth-west",
        name: "Wentworth West",
        location: { country: "GBR", region: "Surrey", climate: "temperate" },
        par: 72,
        courseRating: 74.8,
        slopeRating: 140,
        yardage: 7302,
        skillWeights: {
            distance: 0.7,
            accuracy: 0.65,
            approach: 0.75,
            aroundGreen: 0.6,
            putting: 0.7
        },
        weatherExposure: 0.4,
        courseType: "parkland"
    }),

    // Generic presets for quick setup
    easyParkland: createCourseProfile({
        id: "easy-parkland",
        name: "Friendly Parkland",
        par: 72,
        courseRating: 69.5,
        slopeRating: 113,
        yardage: 6200,
        skillWeights: {
            distance: 0.4,
            accuracy: 0.4,
            approach: 0.5,
            aroundGreen: 0.5,
            putting: 0.5
        },
        weatherExposure: 0.3,
        courseType: "parkland"
    }),

    mediumParkland: createCourseProfile({
        id: "medium-parkland",
        name: "County Golf Club",
        par: 72,
        courseRating: 72.0,
        slopeRating: 125,
        yardage: 6800,
        skillWeights: {
            distance: 0.5,
            accuracy: 0.5,
            approach: 0.55,
            aroundGreen: 0.55,
            putting: 0.55
        },
        weatherExposure: 0.4,
        courseType: "parkland"
    }),

    hardChampionship: createCourseProfile({
        id: "hard-championship",
        name: "Championship Course",
        par: 72,
        courseRating: 74.5,
        slopeRating: 142,
        yardage: 7400,
        skillWeights: {
            distance: 0.7,
            accuracy: 0.7,
            approach: 0.75,
            aroundGreen: 0.7,
            putting: 0.65
        },
        weatherExposure: 0.5,
        courseType: "parkland"
    })
};

/**
 * Course type metadata for display
 */
export const COURSE_TYPES = {
    parkland: {
        label: "Parkland",
        description: "Tree-lined fairways, defined by terrain and vegetation",
        colour: "#228B22"
    },
    links: {
        label: "Links",
        description: "Coastal, exposed to wind, firm and fast conditions",
        colour: "#DAA520"
    },
    coastal: {
        label: "Coastal",
        description: "Ocean views, wind influence, cliff-side holes",
        colour: "#4682B4"
    },
    desert: {
        label: "Desert",
        description: "Target golf through cacti and sand",
        colour: "#CD853F"
    },
    heathland: {
        label: "Heathland",
        description: "Sandy soil, heather rough, pine trees",
        colour: "#8B4513"
    },
    mountain: {
        label: "Mountain",
        description: "Elevation changes, thin air affects ball flight",
        colour: "#696969"
    }
};

/**
 * Calculate course fit score between a golfer and course
 * Returns a value indicating how well suited the golfer is
 * Positive = course suits player, Negative = course doesn't suit
 */
export const calculateCourseFit = (golfer, course) => {
    const { skills } = golfer;
    const { skillWeights, courseType } = course;

    // Map golfer skills to course skill weights
    // offTheTee contributes to both distance and accuracy
    const fit =
        skills.offTheTee * 0.7 * skillWeights.distance +
        skills.offTheTee * 0.3 * skillWeights.accuracy +
        skills.approach * skillWeights.approach +
        skills.aroundGreen * skillWeights.aroundGreen +
        skills.putting * skillWeights.putting;

    // Normalise by total weights
    const totalWeight = skillWeights.distance + skillWeights.accuracy +
        skillWeights.approach + skillWeights.aroundGreen + skillWeights.putting;

    const normalisedFit = fit / totalWeight;

    // Add course type bonus if golfer has relevant situational skill
    let typeBonus = 0;
    if (courseType === "links" && golfer.situational?.weatherResilience > 0.6) {
        typeBonus = 0.1;
    }

    return normalisedFit + typeBonus;
};

/**
 * Get course fit description
 */
export const getCourseFitDescription = (fitScore) => {
    if (fitScore >= 0.3) return "Excellent Fit";
    if (fitScore >= 0.15) return "Good Fit";
    if (fitScore >= 0) return "Neutral";
    if (fitScore >= -0.15) return "Slight Disadvantage";
    if (fitScore >= -0.3) return "Poor Fit";
    return "Very Poor Fit";
};

/**
 * Calculate expected adjustment to scoring based on course fit
 * Returns strokes adjustment (negative = better than expected)
 */
export const calculateFitAdjustment = (fitScore) => {
    // Each 0.1 of fit translates to roughly 0.5 strokes
    return -fitScore * 5;
};

/**
 * Get skill weight descriptions for display
 */
export const SKILL_WEIGHT_LABELS = {
    distance: { label: "Distance", icon: "🏌️", description: "Rewards long hitters" },
    accuracy: { label: "Accuracy", icon: "🎯", description: "Punishes wayward shots" },
    approach: { label: "Approach", icon: "⛳", description: "Demands precise iron play" },
    aroundGreen: { label: "Short Game", icon: "🏌️", description: "Tests chipping and bunker play" },
    putting: { label: "Putting", icon: "🕳️", description: "Complex or fast greens" }
};

/**
 * Generate weather conditions for a tournament
 */
export const generateWeatherConditions = (rounds = 4, baseExposure = 0.5) => {
    return Array.from({ length: rounds }, (_, i) => {
        // Wind varies by day, scaled by course exposure
        const baseWind = 5 + Math.random() * 15;  // 5-20 mph base
        const wind = Math.round(baseWind * (0.5 + baseExposure));

        // Rain chance increases with exposure
        const rainChance = baseExposure * 0.3;
        const rain = Math.random() < rainChance;

        // Temperature varies reasonably
        const temperature = Math.round(60 + Math.random() * 25);  // 60-85°F

        return {
            round: i + 1,
            wind,
            rain,
            temperature,
            description: getWeatherDescription(wind, rain)
        };
    });
};

/**
 * Get weather description text
 */
const getWeatherDescription = (wind, rain) => {
    let desc = "";
    if (wind < 8) desc = "Calm";
    else if (wind < 15) desc = "Breezy";
    else if (wind < 22) desc = "Windy";
    else desc = "Very Windy";

    if (rain) desc += ", Rain";

    return desc;
};

/**
 * Calculate weather impact on scoring
 * Returns strokes to add based on conditions
 */
export const calculateWeatherImpact = (conditions, golfer, course) => {
    const { wind, rain } = conditions;
    const { weatherExposure } = course;
    const resilience = golfer.situational?.weatherResilience ?? 0.5;

    // Wind impact: ~1.15 strokes per 8.6 mph (research-based)
    const windPenalty = (wind / 8.6) * 1.15;

    // Scale by course exposure and golfer resilience
    const effectiveWindPenalty = windPenalty * weatherExposure * (1 - resilience * 0.4);

    // Rain adds 0.3-0.8 strokes depending on severity
    const rainPenalty = rain ? 0.5 * (1 - resilience * 0.3) : 0;

    return effectiveWindPenalty + rainPenalty;
};
